import { useDeliveryApi } from 'infrastructure/api';
import { cloneDeep } from 'lodash';
import {
  ConfirmStakeholdersResponse,
  ProjectStakeholdersSearchResponse,
  StakeholderPayload,
  StakeholderType,
} from './types';
import stakeholdersArrayValidation from './stakeholdersValidation';
import { ValidationSchema } from 'validation-schema-library';

interface StakeholdersService {
  getStakeholders: (projectId: string | number) => Promise<ProjectStakeholdersSearchResponse>;
  stakeholdersBatch: (projectId: string, payload: Array<StakeholderPayload>) => Promise<StakeholderType[]>;
  confirmStakeholders: (projectId: string) => Promise<ConfirmStakeholdersResponse>;
  getStakeholdersValidationSchema: () => Promise<ValidationSchema>;
}

const useStakeholdersService = (): StakeholdersService => {
  const { sendPostRequest } = useDeliveryApi();

  const getStakeholders = async (projectId: string | number): Promise<ProjectStakeholdersSearchResponse> => {
    const response = await sendPostRequest(`/projects/${projectId}/stakeholders/search`);
    return (await response.json()) as ProjectStakeholdersSearchResponse;
  };

  const stakeholdersBatch = async (
    projectId: string,
    payload: Array<StakeholderPayload>,
  ): Promise<StakeholderType[]> => {
    const response = await sendPostRequest(`/projects/${projectId}/stakeholders/batch`, {
      batch: cloneDeep(payload),
    });

    const data = await response.json();
    return Array.isArray(data) ? data : (data?.result ?? []);
  };

  const confirmStakeholders = async (projectId: string): Promise<ConfirmStakeholdersResponse> => {
    const response = await sendPostRequest(`/projects/${projectId}/stakeholders/confirm`);
    return response.json();
  };

  const getStakeholdersValidationSchema = () => Promise.resolve(stakeholdersArrayValidation);

  return {
    getStakeholders,
    stakeholdersBatch,
    confirmStakeholders,
    getStakeholdersValidationSchema,
  };
};

export default useStakeholdersService;
